var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { cloneEvent } from './TelemetryEvent';
import { TenantTokenManager } from './TenantTokenManager';
import { TelemetryEventValidator } from './TelemetryEventValidator';
import { Event } from './Event';
import { logNotification, LogLevel, Category, logError } from './OTelNotifications';
import { makeStringDataField } from '.';
import { oteljsVersion } from './Version';
/**
 * Use this as the Nexus tenant token when Nexus logging is not needed or relevant
 */
export var SuppressNexus = -1;
/**
 * SimpleTelemetryLogger is the base implementation of a telemetry logger.
 * It exposes sendTelemetryEvent, which is used to log a telemetry event.
 */
var SimpleTelemetryLogger = /** @class */ (function () {
    /**
     * Creates a telemetry logger
     * @param parent - A parent telemetry logger that this telemetry logger should inherit from
     *                 This telemetry logger will send telemetry to the same sinks
     *                 and will send the same persistent data fields as the parent logger
     * @param persistentDataFields - Data fields to send with every event
     */
    function SimpleTelemetryLogger(parent, persistentDataFields, config) {
        var _a, _b;
        /**
         * A list of sinks that telemetry events will be forwarded to
         */
        this.onSendEvent = new Event();
        /**
         * @internal Internal variable; do not use publicly.
         */
        this.persistentDataFields = [];
        this.config = config || {};
        if (parent) {
            this.onSendEvent = parent.onSendEvent;
            (_a = this.persistentDataFields).push.apply(_a, parent.persistentDataFields);
            this.config = __assign(__assign({}, parent.getConfig()), this.config);
        }
        if (persistentDataFields) {
            (_b = this.persistentDataFields).push.apply(_b, persistentDataFields);
        }
    }
    /**
     * SendTelemetryEvent is the basic way to send a telemetry event.
     * This should be used for sending telemetry about discrete events that happened
     * For telemetry that includes duration and/or success/failure, the Activity API may be more appropriate.
     * @param event - The telemetry event to send
     */
    SimpleTelemetryLogger.prototype.sendTelemetryEvent = function (event) {
        var localEvent;
        try {
            if (this.onSendEvent.getListenerCount() === 0) {
                logNotification(LogLevel.Warning, Category.Core, function () { return 'No telemetry sinks are attached.'; });
                return;
            }
            // Don't modify the event that got passed in; we create our own.
            localEvent = this.cloneEvent(event);
            this.processTelemetryEvent(localEvent);
        }
        catch (error) {
            logError(Category.Core, 'SendTelemetryEvent', error);
            return;
        }
        try {
            this.onSendEvent.fireEvent(localEvent);
        }
        catch (_e) {
            // Sinks should handle their own errors;
        }
    };
    /**
     * Processes a telemetry event, but does not send it
     *   Determines the tenant tokens
     *   Adds persistent data fields
     *   Validates the event
     * Throws if the event is invalid
     * @param event - The telemetry event to process
     */
    SimpleTelemetryLogger.prototype.processTelemetryEvent = function (event) {
        var _a;
        if (!event.telemetryProperties) {
            event.telemetryProperties = TenantTokenManager.getTenantTokens(event.eventName);
        }
        if (event.dataFields) {
            event.dataFields.unshift(makeStringDataField('OTelJS.Version', oteljsVersion));
            if (this.persistentDataFields) {
                // The last-added data field wins, if the same-named data field is added more than once.
                // Persistent data fields should have lower priority than per-event data fields
                (_a = event.dataFields).unshift.apply(_a, this.persistentDataFields);
            }
        }
        if (!this.config.disableValidation) {
            TelemetryEventValidator.validateTelemetryEvent(event);
        }
    };
    /**
     * Adds a telemetry sink, to which events will be sent
     * @param sink - The telemetry sink to add
     */
    SimpleTelemetryLogger.prototype.addSink = function (sink) {
        this.onSendEvent.addListener(function (event) { return sink.sendTelemetryEvent(event); });
    };
    /**
     * Sets a tenant token for a single namespace. Events within the namespace will be sent to the corresponding tenant.
     * @param namespace - The namespace to which the tokens apply, such as Office.System.
     * @param ariaTenantToken - The Aria tenant token
     * @param nexusTenantToken - The Nexus tenant token
     */
    SimpleTelemetryLogger.prototype.setTenantToken = function (namespace, ariaTenantToken, nexusTenantToken) {
        TenantTokenManager.setTenantToken(namespace, ariaTenantToken, nexusTenantToken);
    };
    /**
     * Sets tenant tokens for multiple namespaces.
     * @param tokenTree - An that represents namespaces and which tenants to send corresponding tokens to
     *   For example, the following tree identifies where to send events under the Office.System and Office.Word namespaces.
     *                \{ Office: \{
     *                    System: \{
     *                      ariaTenantToken: 'foo',
     *                      nexusTenantToken: 100
     *                    \},
     *                    Word: \{
     *                      ariaTenantToken: 'bar',
     *                      nexusTenantToken: 200
     *                    \}
     *                \}\}
     */
    SimpleTelemetryLogger.prototype.setTenantTokens = function (tokenTree) {
        TenantTokenManager.setTenantTokens(tokenTree);
    };
    /**
     * Clone a telemetry event; generally so we don't modify the input telemetry event
     * Also sets dataFields to [] if it isn't already set
     * @param event - The event to clone
     */
    SimpleTelemetryLogger.prototype.cloneEvent = function (event) {
        return cloneEvent(event);
    };
    /**
     * Gets the currently applied configuration
     */
    SimpleTelemetryLogger.prototype.getConfig = function () {
        return this.config;
    };
    return SimpleTelemetryLogger;
}());
export { SimpleTelemetryLogger };
//# sourceMappingURL=SimpleTelemetryLogger.js.map